/**
 * CardCaptureForm - Stripe card capture component for registration
 * Saves card via SetupIntent (no charge). Payment taken later by admin.
 */
import { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Lock, AlertCircle } from 'lucide-react';
import api from '../../lib/api';
import { formatStripeError } from '../../lib/stripeErrors';
import { InAppBrowserWarning } from '../InAppBrowserWarning';

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      '::placeholder': { color: '#9ca3af' },
      iconColor: '#FF007F',
    },
    invalid: { color: '#ef4444', iconColor: '#ef4444' },
  },
  disableLink: true,
};

export const CardCaptureForm = ({ onSuccess, pendingId, studentName, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!pendingId) {
      setError('Registration session not found. Please go back and try again.');
      return;
    }
    initSetupIntent();
  }, [pendingId]);

  const initSetupIntent = async () => {
    if (!pendingId) return;
    
    try {
      const res = await api.post('/payments/guest-card-setup', {
        pending_id: pendingId,
        student_name: studentName
      });
      setClientSecret(res.data.client_secret);
    } catch (err) {
      console.error('Failed to init setup intent:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to initialize card setup. Please try again.';
      setError(errorMsg);
      if (err.response?.status === 404 && onError) {
        onError('pending_not_found');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setLoading(true);
    setError(null);

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (stripeError) {
        setError(formatStripeError(stripeError));
        setLoading(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        const confirmRes = await api.post('/payments/confirm-guest-card', {
          pending_id: pendingId,
          payment_method_id: setupIntent.payment_method
        });
        toast.success('Card saved successfully!');
        onSuccess(confirmRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <InAppBrowserWarning />
      <div className="p-4 bg-white/5 rounded-lg border border-white/10">
        <CardElement options={cardElementOptions} />
      </div>
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      <Button
        type="submit"
        disabled={!stripe || !clientSecret || loading}
        className="w-full bg-[#FF007F] hover:bg-[#D6006A] text-white h-12"
        data-testid="save-card-button"
      >
        {loading ? 'Saving...' : 'Save Card & Continue'}
      </Button>
      <p className="text-xs text-white/50 text-center flex items-center justify-center gap-1">
        <Lock className="w-3 h-3" />
        Your card will only be charged after admin authorization
      </p>
    </form>
  );
};

export default CardCaptureForm;
